import React, { useState, useEffect } from 'react'
import {
  Card,
  Button,
  Space,
  Tag,
  Row,
  Col,
  Tabs,
  Steps,
  Timeline,
  Empty,
  Modal,
  Form,
  Input,
  message,
  Divider,
  Avatar,
  DatePicker,
  InputNumber,
  Select,
} from 'antd'
import {
  EditOutlined,
  FilePdfOutlined,
  PrinterOutlined,
  UploadOutlined,
  PlusOutlined,
  UserAddOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  SyncOutlined,
  FileProtectOutlined,
} from '@ant-design/icons'
import { useNavigate, useParams } from 'react-router-dom'
import { Agreement, AgreementStatus, AgreementTemplate, AgreementAmendment } from '@/types'
import { agreementStore, type AgreementRow } from './agreementStore'
import { notifySuccess } from '@/utils/notify'
import { useModulePermission } from '@/hooks/useModulePermission'
import { useMasterData } from '@/context/MasterDataContext'
import { WorkflowChainDisplay } from '@/components/WorkflowChainDisplay'
import DocumentUploader from '@/components/DocumentUploader'
import { generateAgreementHTML, printAgreementPDF } from '@/utils/agreementPdfGenerator'
import { api, ApiError } from '@/services/api'

const daysAgo = (n: number) => {
  const d = new Date()
  d.setDate(d.getDate() - n)
  return d
}
const daysAhead = (n: number) => {
  const d = new Date()
  d.setDate(d.getDate() + n)
  return d
}

const mockAmendment: AgreementAmendment = {
  id: 'AMD001',
  revisionNumber: 1,
  changeDescription:
    'Updated tariff rate from ₹5.20 to ₹5.35 per kWh due to grid charge revision',
  signedAt: daysAgo(5),
  signedCopyUrl: '/uploads/A001_amend_1.pdf',
  createdAt: daysAgo(6),
}

const mockAgreement: Agreement = {
  id: 'A001',
  code: 'A001',
  proposalId: 'P001',
  leadId: 'L001',
  templateType: AgreementTemplate.TRIPARTITE,
  status: AgreementStatus.PENDING_SCAN,
  generatedAt: daysAgo(10),
  signedAt: daysAgo(8),
  startDate: daysAgo(8),
  endDate: daysAhead(357),
  renewalReminder: 30,
  signedCopyUrl: '/uploads/A001_signed.pdf',
  amendments: [mockAmendment],
  createdAt: daysAgo(10),
  updatedAt: daysAgo(5),
}

// Map a stored agreement row (string dates) → the display Agreement shape.
const toAgreement = (r: AgreementRow): Agreement => ({
  id: r.id,
  code: r.code || r.id,
  proposalId: r.proposalId,
  leadId: r.leadId,
  templateType: (r.templateType as AgreementTemplate) || AgreementTemplate.CONSUMER,
  status: (r.status as AgreementStatus) || AgreementStatus.DRAFT,
  generatedAt: r.generatedAt ? new Date(r.generatedAt) : new Date(),
  signedAt: r.signedAt ? new Date(r.signedAt) : undefined,
  startDate: r.startDate ? new Date(r.startDate) : new Date(),
  endDate: r.endDate ? new Date(r.endDate) : new Date(),
  validityDays: r.validityDays,
  renewalReminder: r.renewalReminder ?? 30,
  stampPaperValue: r.stampPaperValue,
  stampPaperNumber: r.stampPaperNumber || undefined,
  placeOfExecution: r.placeOfExecution || undefined,
  notes: r.notes || undefined,
  signedCopyUrl: r.signedCopyUrl || undefined,
  amendments: (r.amendments ?? []).map((a) => ({
    id: a.id,
    revisionNumber: a.revisionNumber,
    changeDescription: a.changeDescription,
    price: (a as any).price,
    modeOfSupply: (a as any).modeOfSupply,
    typeOfPower: (a as any).typeOfPower,
    validFrom: a.validFrom ? new Date(a.validFrom) : undefined,
    validTo: a.validTo ? new Date(a.validTo) : undefined,
    signedCopyUrl: a.signedCopyUrl,
    signedAt: a.signedAt ? new Date(a.signedAt) : undefined,
    createdAt: a.createdAt ? new Date(a.createdAt) : new Date(),
  })),
  createdAt: r.createdAt ? new Date(r.createdAt) : new Date(),
  updatedAt: r.updatedAt ? new Date(r.updatedAt) : new Date(),
})

const formatDate = (date: Date) =>
  new Date(date).toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  })

const getDaysLeft = (endDate: Date): number => {
  const now = new Date()
  now.setHours(0, 0, 0, 0)
  const end = new Date(endDate)
  end.setHours(0, 0, 0, 0)
  return Math.ceil((end.getTime() - now.getTime()) / (1000 * 60 * 60 * 24))
}

const getDuration = (start: Date, end: Date): number => {
  const s = new Date(start)
  s.setHours(0, 0, 0, 0)
  const e = new Date(end)
  e.setHours(0, 0, 0, 0)
  return Math.ceil((e.getTime() - s.getTime()) / (1000 * 60 * 60 * 24))
}

const statusConfig: Record<AgreementStatus, { color: string; icon?: React.ReactNode }> = {
  [AgreementStatus.ACTIVE]: { color: 'success', icon: <CheckCircleOutlined /> },
  [AgreementStatus.READY_FOR_SIGNING]: { color: 'processing', icon: <SyncOutlined spin /> },
  [AgreementStatus.PENDING_SCAN]: { color: 'warning', icon: <ClockCircleOutlined /> },
  [AgreementStatus.SIGNED]: { color: 'blue', icon: <CheckCircleOutlined /> },
  [AgreementStatus.DRAFT]: { color: 'default' },
  [AgreementStatus.EXPIRED]: { color: 'error' },
  [AgreementStatus.AMENDED]: { color: 'cyan' },
}

interface PartyCard {
  role: string
  name: string
  rep: string
  designation: string
  contact?: string
  tagColor: string
  avatarColor: string
}

const AgreementDetail: React.FC = () => {
  const navigate = useNavigate()
  const { id } = useParams()
  const stored = id ? agreementStore.get(id) : undefined
  const { canDownload } = useModulePermission('Agreements')
  const { supplyModes, powerTypes } = useMasterData()
  const [agreement, setAgreement] = useState<Agreement>(() => (stored ? toAgreement(stored) : mockAgreement))
  const [uploadModalOpen, setUploadModalOpen] = useState(false)
  // Real uploaded-file URL (from DocumentUploader), shared by both the modal
  // and the inline "Action Required" card — whichever one the user uses.
  const [pendingSignedCopyUrl, setPendingSignedCopyUrl] = useState<string | undefined>()
  const [pendingAmendmentCopyUrl, setPendingAmendmentCopyUrl] = useState<string | undefined>()
  const [uploading, setUploading] = useState(false)

  // After a hard refresh the in-memory store is empty, so hydrate from the DB and
  // re-sync this agreement from the real record once it arrives (instead of the
  // mock placeholder). Also keeps status in sync after an upload elsewhere.
  useEffect(() => {
    const sync = () => {
      const s = id ? agreementStore.get(id) : undefined
      if (s) setAgreement(toAgreement(s))
    }
    agreementStore.refresh().then(sync)
    window.addEventListener(agreementStore.eventName, sync)
    return () => window.removeEventListener(agreementStore.eventName, sync)
  }, [id])

  const parties: PartyCard[] = stored?.parties?.length ? stored.parties.map(p => ({
    role: p.role,
    name: p.name,
    rep: p.rep || '—',
    designation: p.designation || '—',
    contact: [p.email, p.phone].filter(Boolean).join(' | '),
    tagColor: p.role === 'Generator' ? 'green' : p.role === 'Consumer' ? 'blue' : 'purple',
    avatarColor: p.role === 'Generator' ? '#00A651' : p.role === 'Consumer' ? '#0099CC' : '#001A4D',
  })) : [
    {
      role: agreement.templateType === AgreementTemplate.GENERATOR ? 'Generator' : 'Consumer',
      name: stored?.partyName || 'Counterparty',
      rep: '—',
      designation: '—',
      tagColor: agreement.templateType === AgreementTemplate.GENERATOR ? 'green' : 'blue',
      avatarColor: agreement.templateType === AgreementTemplate.GENERATOR ? '#00A651' : '#0099CC',
    },
    {
      role: 'Facilitator',
      name: 'Shakthi Power Solutions Pvt Ltd',
      rep: 'Authorised Signatory',
      designation: 'CEO',
      tagColor: 'purple',
      avatarColor: '#001A4D',
    },
  ]
  const [amendModalOpen, setAmendModalOpen] = useState(false)
  const [amendForm] = Form.useForm()

  // Once the store has genuinely finished loading and this id still isn't a
  // real record (bad link, or the agreement was deleted), stop — don't keep
  // rendering the mock placeholder above as if it were real data.
  if (id && agreementStore.loaded && !stored) {
    return (
      <div style={{ padding: 48, textAlign: 'center' }}>
        <Empty description="Agreement not found" />
        <Button type="primary" style={{ marginTop: 16 }} onClick={() => navigate('/agreements')}>
          Back to Agreements
        </Button>
      </div>
    )
  }

  const cfg = statusConfig[agreement.status]
  const daysLeft = getDaysLeft(agreement.endDate)
  const duration = agreement.validityDays ?? getDuration(agreement.startDate, agreement.endDate)

  const canUpload =
    agreement.status === AgreementStatus.DRAFT ||
    agreement.status === AgreementStatus.READY_FOR_SIGNING ||
    agreement.status === AgreementStatus.PENDING_SCAN
  const canAmend =
    agreement.status === AgreementStatus.ACTIVE ||
    agreement.status === AgreementStatus.SIGNED
  const canConvert = agreement.status === AgreementStatus.SIGNED

  const handleMarkPhysicallySigned = () => {
    const todayStr = new Date().toISOString().slice(0, 10)
    setAgreement((prev) => ({
      ...prev,
      status: AgreementStatus.PENDING_SCAN,
      signedAt: new Date(),
    }))
    if (stored) {
      agreementStore.save({
        id: agreement.id,
        status: AgreementStatus.PENDING_SCAN,
        signedAt: todayStr,
      })
    }
    notifySuccess('Agreement marked as signed', 'Physical signing completed — waiting for scanned copy upload')
  }

  const handleUploadSubmit = () => {
    if (!pendingSignedCopyUrl) {
      message.error('Please upload the signed copy file first.')
      return
    }
    setUploading(true)
    try {
      const todayStr = new Date().toISOString().slice(0, 10)
      const fileUrl = pendingSignedCopyUrl
      setAgreement((prev) => ({
        ...prev,
        status: AgreementStatus.ACTIVE,
        signedAt: prev.signedAt ?? new Date(),
        signedCopyUrl: fileUrl,
      }))
      // Persist to the store / DB.
      if (stored) {
        agreementStore.save({
          id: agreement.id,
          status: AgreementStatus.ACTIVE,
          signedAt: stored.signedAt || todayStr,
          signedCopyUrl: fileUrl,
        })
      }
      notifySuccess('Agreement activated', 'Signed copy uploaded — the agreement is now Active')
      setUploadModalOpen(false)
      setPendingSignedCopyUrl(undefined)
    } catch (err) {
      message.error('Failed to upload the signed copy. Please try again.')
    } finally {
      setUploading(false)
    }
  }

  const handleAmendSubmit = () => {
    amendForm.validateFields().then((vals) => {
      const todayStr = new Date().toISOString().slice(0, 10)
      const existing = stored?.amendments ?? []
      const rev = existing.length + 1
      const hasCopy = !!pendingAmendmentCopyUrl

      // Store shape: dates as YYYY-MM-DD strings (round-trips cleanly to the DB).
      const amendmentRow = {
        id: `AMD${Date.now().toString(36).toUpperCase()}`,
        revisionNumber: rev,
        changeDescription: vals.changeDescription,
        price: vals.price != null ? Number(vals.price) : undefined,
        modeOfSupply: vals.modeOfSupply || undefined,
        typeOfPower: vals.typeOfPower || undefined,
        validFrom: vals.validFrom ? vals.validFrom.format('YYYY-MM-DD') : undefined,
        validTo: vals.validTo ? vals.validTo.format('YYYY-MM-DD') : undefined,
        signedCopyUrl: hasCopy ? pendingAmendmentCopyUrl : undefined,
        signedAt: hasCopy ? todayStr : undefined,
        createdAt: todayStr,
      }
      const amendments = [...existing, amendmentRow]

      // Persist to the store / DB (append to this agreement's amendment list).
      if (stored) agreementStore.save({ id: agreement.id, amendments })

      // Optimistic UI update (the store-sync effect also refreshes this).
      setAgreement((prev) => ({
        ...prev,
        amendments: [
          ...prev.amendments,
          {
            ...amendmentRow,
            validFrom: amendmentRow.validFrom ? new Date(amendmentRow.validFrom) : undefined,
            validTo: amendmentRow.validTo ? new Date(amendmentRow.validTo) : undefined,
            signedAt: amendmentRow.signedAt ? new Date(amendmentRow.signedAt) : undefined,
            createdAt: new Date(amendmentRow.createdAt),
          },
        ],
      }))

      message.success('Amendment recorded successfully')
      setAmendModalOpen(false)
      amendForm.resetFields()
      setPendingAmendmentCopyUrl(undefined)
    }).catch(() => {
      message.error('Please fill in all required amendment fields.')
    })
  }

  const agreementPdfData = {
    // toAgreement() doesn't carry parties/agreementType (they're not on the
    // `Agreement` type) — pull them from the raw store row so the generated
    // document's Parties table and Type line aren't silently empty.
    agreement: { ...agreement, agreementType: stored?.agreementType, parties: stored?.parties },
    code: agreement.code || agreement.id,
  }

  const handlePrintAgreement = () => {
    try {
      printAgreementPDF(generateAgreementHTML(agreementPdfData))
    } catch {
      message.error('Failed to open for printing')
    }
  }

  const handleDownloadDocument = async (docUrl?: string, filename?: string) => {
    if (docUrl) {
      // docUrl points at the backend's file proxy, a different origin than
      // the frontend — browsers ignore the <a download> attribute on
      // cross-origin links (it just navigates/opens the file instead of
      // downloading it). Fetch the bytes first, then trigger the download
      // from a same-origin blob: URL, which download always respects.
      try {
        const res = await fetch(docUrl)
        if (!res.ok) throw new Error(`Failed to fetch file (${res.status})`)
        const blob = await res.blob()
        const blobUrl = URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = blobUrl
        link.download = filename || `Agreement_${agreement.code || agreement.id}.pdf`
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        URL.revokeObjectURL(blobUrl)
        message.success('Document download started')
      } catch (err) {
        console.error('Failed to download document:', err)
        message.error('Failed to download the file. Please try again.')
      }
      return
    }
    // No uploaded signed copy — download the generated agreement document
    // itself. Rendered server-side via Puppeteer (real headless Chromium) —
    // html2pdf.js/html2canvas (downloadAgreementAsPDF) came back blank in
    // practice, same issue already fixed for Proposals.
    try {
      await api.downloadAgreementPdf(agreement.id, generateAgreementHTML(agreementPdfData), `Agreement_${agreement.code || agreement.id}.pdf`)
      message.success('Agreement PDF downloaded successfully!')
    } catch (err) {
      console.error('Agreement PDF download failed:', err)
      message.error(err instanceof ApiError ? err.message : 'Failed to generate PDF download. Please try again.')
    }
  }

  const handleConvert = () => {
    navigate(`/leads/${agreement.leadId}`)
  }

  // Steps status computation
  const stepStatus = (idx: number): 'finish' | 'process' | 'wait' | 'error' => {
    const hasGenerated = !!agreement.generatedAt
    const hasSigned = !!agreement.signedAt
    const hasScan = !!agreement.signedCopyUrl
    const isActive = agreement.status === AgreementStatus.ACTIVE

    if (idx === 0) return hasGenerated ? 'finish' : 'process'
    if (idx === 1) return hasSigned ? 'finish' : hasGenerated ? 'process' : 'wait'
    if (idx === 2) return hasSigned ? 'finish' : 'wait'
    if (idx === 3) {
      if (hasScan) return 'finish'
      if (agreement.status === AgreementStatus.PENDING_SCAN) return 'process'
      return 'wait'
    }
    if (idx === 4) return isActive ? 'finish' : 'wait'
    return 'wait'
  }

  const kpiCards = [
    {
      label: 'Validity',
      value: `${duration} days`,
      color: 'var(--brand-primary)',
      border: 'var(--brand-primary)',
    },
    {
      label: 'Days Remaining',
      value: daysLeft < 0 ? 'Expired' : `${daysLeft} days`,
      color: daysLeft < 30 ? '#ff4d4f' : 'var(--brand-green)',
      border: daysLeft < 30 ? '#ff4d4f' : 'var(--brand-green)',
    },
    {
      label: 'Renewal Reminder',
      value: `${agreement.renewalReminder} days before expiry`,
      color: '#fa8c16',
      border: '#fa8c16',
    },
    {
      label: 'Amendments',
      value: `${agreement.amendments.length}`,
      color: '#722ed1',
      border: '#722ed1',
    },
  ]

  const tabItems = [
    {
      key: 'workflow',
      label: 'Execution Workflow',
      children: (
        <div>
          <Steps
            current={
              agreement.status === AgreementStatus.ACTIVE
                ? 4
                : agreement.status === AgreementStatus.PENDING_SCAN
                ? 3
                : agreement.signedAt
                ? 2
                : agreement.generatedAt
                ? 1
                : 0
            }
            style={{ marginBottom: 32 }}
            items={[
              {
                title: 'Agreement Generated',
                description: agreement.generatedAt ? formatDate(agreement.generatedAt) : '',
                status: stepStatus(0),
              },
              {
                title: 'Stamp Paper Printing',
                status: stepStatus(1),
              },
              {
                title: 'Physical Signing',
                description: agreement.signedAt ? formatDate(agreement.signedAt) : '',
                status: stepStatus(2),
              },
              {
                title: 'Signed Copy Upload',
                status: stepStatus(3),
              },
              {
                title: 'Agreement Active',
                status: stepStatus(4),
              },
            ]}
          />

          {/* Only what was actually entered on the agreement — no demo
              fallback, and no card at all when none of these four are set. */}
          {(agreement.stampPaperValue != null || agreement.stampPaperNumber || agreement.placeOfExecution || agreement.notes) && (
            <Card
              style={{ borderRadius: 'var(--radius-lg)', boxShadow: 'var(--shadow-sm)', marginBottom: 24 }}
            >
              <div style={{ fontWeight: 700, fontSize: 14, color: 'var(--text-primary)', marginBottom: 12 }}>
                Contract Execution Details
              </div>
              {[
                { label: 'Stamp Paper Value', value: agreement.stampPaperValue != null ? `₹${agreement.stampPaperValue}` : undefined },
                { label: 'Stamp Paper Number', value: agreement.stampPaperNumber },
                { label: 'Place of Execution', value: agreement.placeOfExecution },
                { label: 'Notes / Special Clauses', value: agreement.notes },
              ]
                .filter((row) => row.value)
                .map((row) => (
                  <div
                    key={row.label}
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      gap: 16,
                      padding: '6px 0',
                      borderBottom: '1px solid var(--border-default)',
                      fontSize: 13,
                    }}
                  >
                    <span style={{ color: 'var(--text-muted)', flexShrink: 0 }}>{row.label}</span>
                    <span style={{ color: 'var(--text-primary)', fontWeight: 500, textAlign: 'right' }}>
                      {row.value}
                    </span>
                  </div>
                ))}
            </Card>
          )}

          <WorkflowChainDisplay moduleName="Agreement" />

          {agreement.status === AgreementStatus.READY_FOR_SIGNING && (
            <Card
              style={{
                borderLeft: '4px solid var(--brand-primary)',
                borderRadius: 'var(--radius-lg)',
                background: '#f0f5ff',
              }}
            >
              <div style={{ marginBottom: 12 }}>
                <span
                  style={{ fontWeight: 700, fontSize: 15, color: 'var(--brand-primary)' }}
                >
                  Step 2 & 3: Stamp Paper Printing & Physical Signing
                </span>
                <p style={{ color: 'var(--text-secondary)', margin: '4px 0 16px', fontSize: 13 }}>
                  Please print the agreement draft on appropriate stamp papers and secure the signatures of all executing parties.
                </p>
              </div>
              <Space wrap>
                <Button
                  type="primary"
                  icon={<PrinterOutlined />}
                  style={{ background: 'var(--brand-primary)', borderColor: 'var(--brand-primary)' }}
                  onClick={handlePrintAgreement}
                >
                  Print Agreement Draft
                </Button>
                <Button
                  icon={<CheckCircleOutlined />}
                  onClick={handleMarkPhysicallySigned}
                >
                  Mark as Physically Signed
                </Button>
              </Space>
            </Card>
          )}

          {agreement.status === AgreementStatus.PENDING_SCAN && (
            <Card
              style={{
                borderLeft: '4px solid #fa8c16',
                borderRadius: 'var(--radius-lg)',
                background: '#fffbe6',
              }}
            >
              <div style={{ marginBottom: 12 }}>
                <span
                  style={{ fontWeight: 700, fontSize: 15, color: '#d46b08' }}
                >
                  Action Required: Upload the signed agreement scan
                </span>
                <p style={{ color: '#7c4700', margin: '4px 0 16px', fontSize: 13 }}>
                  Please upload a clear scan or photo of the stamp-paper signed agreement to activate.
                </p>
              </div>
              <DocumentUploader
                folder="agreements"
                accept=".pdf,.jpg,.jpeg,.png"
                value={pendingSignedCopyUrl}
                onChange={setPendingSignedCopyUrl}
              />
              <div style={{ marginTop: 12, textAlign: 'right' }}>
                <Button
                  type="primary"
                  icon={<UploadOutlined />}
                  style={{ background: '#fa8c16', borderColor: '#fa8c16' }}
                  onClick={handleUploadSubmit}
                  loading={uploading}
                >
                  Upload & Activate
                </Button>
              </div>
            </Card>
          )}

          {agreement.status === AgreementStatus.ACTIVE && (
            <Card
              style={{
                borderLeft: '4px solid var(--brand-green)',
                borderRadius: 'var(--radius-lg)',
                background: '#f6ffed',
              }}
            >
              <span style={{ fontWeight: 700, fontSize: 15, color: '#389e0d' }}>
                <CheckCircleOutlined style={{ marginRight: 8 }} />
                Agreement is Active — all steps completed.
              </span>
            </Card>
          )}
        </div>
      ),
    },
    {
      key: 'parties',
      label: 'Parties',
      children: (
        <Row gutter={[16, 16]}>
          {parties.map((party) => (
            <Col key={party.role} xs={24} lg={8}>
              <Card
                style={{
                  borderRadius: 'var(--radius-lg)',
                  boxShadow: 'var(--shadow-sm)',
                  height: '100%',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    marginBottom: 16,
                  }}
                >
                  <Avatar
                    size={48}
                    style={{ background: party.avatarColor, fontWeight: 700, fontSize: 20 }}
                  >
                    {party.name.charAt(0)}
                  </Avatar>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 14, color: 'var(--text-primary)' }}>
                      {party.name}
                    </div>
                    <Tag color={party.tagColor} style={{ marginTop: 4 }}>
                      {party.role}
                    </Tag>
                  </div>
                </div>
                <Divider style={{ margin: '0 0 12px' }} />
                {[
                  { label: 'Representative', value: party.rep },
                  { label: 'Designation', value: party.designation },
                  ...(party.contact ? [{ label: 'Contact', value: party.contact }] : []),
                ].map((row) => (
                  <div
                    key={row.label}
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      padding: '6px 0',
                      borderBottom: '1px solid var(--border-default)',
                      fontSize: 13,
                    }}
                  >
                    <span style={{ color: 'var(--text-muted)' }}>{row.label}</span>
                    <span style={{ color: 'var(--text-primary)', fontWeight: 500 }}>
                      {row.value}
                    </span>
                  </div>
                ))}
              </Card>
            </Col>
          ))}
        </Row>
      ),
    },
    {
      key: 'amendments',
      label: 'Amendments',
      children: (
        <div>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: 20,
            }}
          >
            <h3 style={{ margin: 0, color: 'var(--text-primary)', fontSize: 16, fontWeight: 700 }}>
              Amendment History
            </h3>
            <Button
              icon={<PlusOutlined />}
              onClick={() => setAmendModalOpen(true)}
            >
              Add Amendment
            </Button>
          </div>

          {agreement.amendments.length > 0 ? (
            <Timeline
              items={agreement.amendments.map((amend) => ({
                dot: (
                  <div
                    style={{
                      width: 10,
                      height: 10,
                      borderRadius: '50%',
                      background: 'var(--brand-accent)',
                    }}
                  />
                ),
                children: (
                  <Card
                    size="small"
                    style={{
                      borderRadius: 8,
                      marginBottom: 8,
                      borderLeft: '3px solid var(--brand-accent)',
                    }}
                  >
                    <div style={{ fontWeight: 700, color: 'var(--text-primary)', marginBottom: 4 }}>
                      Amendment #{amend.revisionNumber}
                    </div>
                    {(amend.validFrom || amend.validTo) && (
                      <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--brand-primary)', marginBottom: 6 }}>
                        Valid: {amend.validFrom ? formatDate(amend.validFrom) : '—'} → {amend.validTo ? formatDate(amend.validTo) : '—'}
                      </div>
                    )}
                    {(amend.price != null || amend.modeOfSupply || amend.typeOfPower) && (
                      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', margin: '4px 0 8px', background: '#F8FAFC', padding: '6px 10px', borderRadius: 6, fontSize: 12 }}>
                        {amend.price != null && <span><strong style={{ color: '#001A4D' }}>Price:</strong> ₹{amend.price}/kWh</span>}
                        {amend.modeOfSupply && <span><strong style={{ color: '#001A4D' }}>Mode of Supply:</strong> {amend.modeOfSupply}</span>}
                        {amend.typeOfPower && <span><strong style={{ color: '#001A4D' }}>Type of Power:</strong> {amend.typeOfPower}</span>}
                      </div>
                    )}
                    <div style={{ color: 'var(--text-secondary)', fontSize: 13, marginBottom: 8 }}>
                      {amend.changeDescription}
                    </div>
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                      }}
                    >
                      {amend.signedAt && (
                        <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>
                          Signed: {formatDate(amend.signedAt)}
                        </span>
                      )}
                      {amend.signedCopyUrl && canDownload && (
                        <Button
                          type="link"
                          size="small"
                          icon={<FilePdfOutlined />}
                          onClick={() => handleDownloadDocument(amend.signedCopyUrl, `Amendment_${amend.revisionNumber}.pdf`)}
                          style={{ padding: 0 }}
                        >
                          Download
                        </Button>
                      )}
                    </div>
                  </Card>
                ),
              }))}
            />
          ) : (
            <Empty description="No amendments recorded" />
          )}
        </div>
      ),
    },
    {
      key: 'documents',
      label: 'Documents',
      children: (
        <Card
          style={{ borderRadius: 'var(--radius-lg)', boxShadow: 'var(--shadow-sm)' }}
        >
          {[
            {
              name: 'Original Agreement',
              description: `Generated on ${formatDate(agreement.generatedAt)}`,
              actions: (
                <Space>
                  {canDownload && <Button size="small" icon={<FilePdfOutlined />} onClick={() => handleDownloadDocument(undefined, `Agreement_${agreement.code || agreement.id}.pdf`)}>Download</Button>}
                  <Button size="small" icon={<PrinterOutlined />} onClick={handlePrintAgreement}>Print</Button>
                </Space>
              ),
            },
            ...(agreement.signedCopyUrl
              ? [
                  {
                    name: 'Signed Copy',
                    description: agreement.signedAt
                      ? `Uploaded on ${formatDate(agreement.signedAt)}`
                      : 'Uploaded',
                    actions: canDownload ? (
                      <Button size="small" icon={<FilePdfOutlined />} onClick={() => handleDownloadDocument(agreement.signedCopyUrl, `Signed_Agreement_${agreement.code || agreement.id}.pdf`)}>
                        Download
                      </Button>
                    ) : null,
                  },
                ]
              : []),
            ...agreement.amendments
              .filter((a) => a.signedCopyUrl)
              .map((a) => ({
                name: `Amendment ${a.revisionNumber}`,
                description: a.signedAt ? `Signed on ${formatDate(a.signedAt)}` : '',
                actions: canDownload ? (
                  <Button size="small" icon={<FilePdfOutlined />} onClick={() => handleDownloadDocument(a.signedCopyUrl, `Amendment_${a.revisionNumber}.pdf`)}>
                    Download
                  </Button>
                ) : null,
              })),
          ].map((doc, idx, arr) => (
            <div key={doc.name}>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '14px 4px',
                  flexWrap: 'wrap',
                  gap: 8,
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <FileProtectOutlined
                    style={{ fontSize: 20, color: 'var(--brand-primary)' }}
                  />
                  <div>
                    <div style={{ fontWeight: 600, color: 'var(--text-primary)' }}>
                      {doc.name}
                    </div>
                    <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                      {doc.description}
                    </div>
                  </div>
                </div>
                {doc.actions}
              </div>
              {idx < arr.length - 1 && <Divider style={{ margin: 0 }} />}
            </div>
          ))}
        </Card>
      ),
    },
  ]

  return (
    <div style={{ padding: '24px', background: 'var(--surface-0)', minHeight: '100vh' }}>
      {/* Header */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          marginBottom: 24,
          flexWrap: 'wrap',
          gap: 16,
        }}
      >
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 6 }}>
            <h2
              style={{
                margin: 0,
                fontSize: 26,
                fontWeight: 700,
                color: 'var(--brand-primary)',
              }}
            >
              Agreement {agreement.code || agreement.id}
            </h2>
            <Tag color={cfg.color} icon={cfg.icon} style={{ fontSize: 13 }}>
              {(() => {
                const status = agreement.status;
                if (status === AgreementStatus.ACTIVE || status === AgreementStatus.SIGNED) return 'Executed';
                if (status === AgreementStatus.READY_FOR_SIGNING || status === AgreementStatus.PENDING_SCAN) return 'Waiting for Sign';
                return status;
              })()}
            </Tag>
            <Tag color="blue" style={{ fontSize: 13 }}>
              {agreement.templateType}
            </Tag>
          </div>
          <div style={{ color: 'var(--text-secondary)', fontSize: 13, marginTop: 4 }}>
            Proposal {agreement.proposalId} &nbsp;|&nbsp; Lead {agreement.leadId} &nbsp;|&nbsp;
            Agreement Date: {formatDate(agreement.startDate)} &nbsp;|&nbsp; Valid Date: {formatDate(agreement.endDate)}
          </div>
        </div>

        <Space wrap>
          <Button icon={<PrinterOutlined />} onClick={handlePrintAgreement}>Print Agreement</Button>
          {agreement.signedCopyUrl && canDownload && (
            <Button icon={<FilePdfOutlined />} onClick={() => handleDownloadDocument(agreement.signedCopyUrl, `Signed_Agreement_${agreement.code || agreement.id}.pdf`)}>
              Download Signed Copy
            </Button>
          )}
          {canUpload && (
            <Button
              icon={<UploadOutlined />}
              style={{ borderColor: '#fa8c16', color: '#fa8c16' }}
              onClick={() => setUploadModalOpen(true)}
            >
              Upload Signed Copy
            </Button>
          )}
          {canAmend && (
            <Button icon={<PlusOutlined />} onClick={() => setAmendModalOpen(true)}>
              Add Amendment
            </Button>
          )}
          {canConvert && (
            <Button
              type="primary"
              icon={<UserAddOutlined />}
              style={{ background: 'var(--brand-green)', borderColor: 'var(--brand-green)' }}
              onClick={handleConvert}
            >
              Convert to Customer
            </Button>
          )}
          <Button
            icon={<EditOutlined />}
            onClick={() => navigate(`/agreements/${agreement.id}/edit`)}
          >
            Edit
          </Button>
        </Space>
      </div>

      {/* KPI stat cards */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        {kpiCards.map((card) => (
          <Col key={card.label} xs={24} sm={12} lg={6}>
            <Card
              style={{
                borderLeft: `3.5px solid ${card.border}`,
                borderRadius: 'var(--radius-lg)',
                boxShadow: 'var(--shadow-sm)',
                background: 'var(--surface-1)',
              }}
              styles={{ body: { padding: '16px 20px' } }}
            >
              <div style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 4 }}>
                {card.label}
              </div>
              <div style={{ fontSize: 20, fontWeight: 700, color: card.color }}>
                {card.value}
              </div>
            </Card>
          </Col>
        ))}
      </Row>

      {/* Tabs */}
      <Card
        style={{
          borderRadius: 'var(--radius-lg)',
          boxShadow: 'var(--shadow-sm)',
          background: 'var(--surface-1)',
        }}
        styles={{ body: { padding: '0 24px 24px' } }}
      >
        <Tabs items={tabItems} size="large" />
      </Card>

      {/* Upload Signed Copy Modal */}
      <Modal
        title="Upload Signed Copy"
        open={uploadModalOpen}
        onCancel={() => { setUploadModalOpen(false); setPendingSignedCopyUrl(undefined) }}
        onOk={handleUploadSubmit}
        confirmLoading={uploading}
        okText="Upload & Activate"
        okButtonProps={{ style: { background: 'var(--brand-green)', borderColor: 'var(--brand-green)' } }}
      >
        <p style={{ color: 'var(--text-secondary)', marginBottom: 12 }}>
          Please upload a clear scan or photo of the stamp-paper signed agreement.
        </p>
        <DocumentUploader
          folder="agreements"
          accept=".pdf,.jpg,.jpeg,.png"
          value={pendingSignedCopyUrl}
          onChange={setPendingSignedCopyUrl}
        />
      </Modal>

      {/* Add Amendment Modal */}
      <Modal
        title="Add Amendment"
        open={amendModalOpen}
        onCancel={() => { setAmendModalOpen(false); amendForm.resetFields(); setPendingAmendmentCopyUrl(undefined) }}
        onOk={handleAmendSubmit}
        okText="Record Amendment"
        okButtonProps={{ style: { background: 'var(--brand-primary)', borderColor: 'var(--brand-primary)' } }}
      >
        <Form form={amendForm} layout="vertical">
          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item
                name="validFrom"
                label="Valid From"
                rules={[{ required: true, message: 'Select the start date' }]}
              >
                <DatePicker style={{ width: '100%' }} format="DD MMM YYYY" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                name="validTo"
                label="Valid To"
                dependencies={['validFrom']}
                rules={[
                  { required: true, message: 'Select the end date' },
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      const from = getFieldValue('validFrom')
                      if (!value || !from || value.isAfter(from)) return Promise.resolve()
                      return Promise.reject(new Error('Valid To must be after Valid From'))
                    },
                  }),
                ]}
              >
                <DatePicker style={{ width: '100%' }} format="DD MMM YYYY" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} sm={8}>
              <Form.Item name="price" label="Price (₹/kWh)">
                <InputNumber style={{ width: '100%' }} placeholder="e.g. 5.35" min={0} step={0.01} precision={2} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={8}>
              <Form.Item name="modeOfSupply" label="Mode of Supply">
                <Select
                  placeholder="Select Mode"
                  allowClear
                  options={supplyModes.filter(m => m.status === 'active').map(m => ({ label: m.name, value: m.name }))}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={8}>
              <Form.Item name="typeOfPower" label="Type of Power">
                <Select
                  placeholder="Select Power Type"
                  allowClear
                  options={powerTypes.filter(p => p.status === 'active').map(p => ({ label: p.name, value: p.name }))}
                />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="changeDescription"
            label="Description"
            rules={[{ required: true, message: 'Please describe the amendment' }]}
          >
            <Input.TextArea rows={3} placeholder="Describe the changes made in this amendment…" />
          </Form.Item>
          <Form.Item label="Signed Amendment Copy">
            <DocumentUploader
              folder="agreements"
              accept=".pdf,.jpg,.jpeg,.png"
              value={pendingAmendmentCopyUrl}
              onChange={setPendingAmendmentCopyUrl}
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  )
}

export default AgreementDetail
